import { supabase } from '@/lib/supabase';
import type { CreateCategoryParams, UpdateCategoryParams, DeleteCategoryParams, UpdateCategoryOrderParams } from '@/features/categories/types';
import { changeTrackingService } from '@/services/changeTracking';

export const categoryMutations = {
  createCategory: async (params: CreateCategoryParams) => {
    const { data: category, error } = await supabase
      .from('categories')
      .insert({
        name: params.name,
        parent_id: params.parentId,
        display_order: params.displayOrder,
        provider_id: params.providerId,
        version_id: params.versionId,
        price: params.price,
        description: params.description,
        is_addon: params.isAddon
      })
      .select('id, name, provider_id, version_id, parent_id, display_order, price, description, is_addon, created_at, updated_at')
      .single();

    if (error) throw error;

    // Track category creation
    try {
      // Get version data for region_id
      const { data: version } = await supabase
        .from('provider_versions')
        .select('region_id')
        .eq('id', params.versionId)
        .single();

      if (version) {
        await changeTrackingService.logEntityCreation({
          versionId: params.versionId,
          providerId: params.providerId,
          regionId: version.region_id,
          entityType: 'categories',
          entityId: category.id,
          entityData: {
            id: category.id,
            name: category.name,
            parent_id: category.parent_id,
            display_order: category.display_order,
            price: category.price,
            description: category.description,
            is_addon: category.is_addon
          }
        });
      }
    } catch (trackingError) {
      console.error('Failed to track category creation:', trackingError);
    }

    return category;
  },

  updateCategory: async (params: UpdateCategoryParams) => {
    // Get old category data for change tracking
    const { data: oldCategory } = await supabase
      .from('categories')
      .select(`
        *,
        provider_versions!version_id(region_id)
      `)
      .eq('id', params.id)
      .single();

    const { data: category, error } = await supabase
      .from('categories')
      .update({
        name: params.name,
        parent_id: params.parentId,
        display_order: params.displayOrder,
        price: params.price,
        description: params.description,
        is_addon: params.isAddon
      })
      .eq('id', params.id)
      .select('id, name, provider_id, version_id, parent_id, display_order, price, description, is_addon, created_at, updated_at')
      .single();

    if (error) throw error;

    // Track category field changes
    if (oldCategory) {
      try {
        const fieldsToTrack = ['name', 'parent_id', 'display_order', 'price', 'description', 'is_addon'];
        const newData = {
          ...oldCategory,
          name: params.name,
          parent_id: params.parentId,
          display_order: params.displayOrder,
          price: params.price,
          description: params.description,
          is_addon: params.isAddon
        };

        const changes = changeTrackingService.generateFieldChanges(
          oldCategory,
          newData,
          fieldsToTrack
        );

        if (changes.length > 0) {
          await changeTrackingService.logBulkChanges({
            versionId: oldCategory.version_id!,
            providerId: oldCategory.provider_id!,
            regionId: (oldCategory.provider_versions as any)[0]?.region_id,
            entityType: 'categories',
            entityId: params.id,
            changes
          });
        }
      } catch (trackingError) {
        console.error('Failed to track category update:', trackingError);
      }
    }

    return category;
  },

  deleteCategory: async ({ categoryIds, providerId, versionId }: DeleteCategoryParams) => {
    // Get version data for region_id
    const { data: version } = await supabase
      .from('provider_versions')
      .select('region_id')
      .eq('id', versionId)
      .single();

    for (const categoryId of categoryIds) {
      // Get category data before deletion for change tracking
      const { data: categoryToDelete } = await supabase
        .from('categories')
        .select('*')
        .eq('id', categoryId)
        .single();

      const { error } = await supabase.rpc('delete_category_with_children', {
        p_category_id: categoryId,
        p_provider_id: providerId,
        p_version_id: versionId
      });
      if (error) throw error;

      // Track category deletion
      if (categoryToDelete && version) {
        try {
          await changeTrackingService.logEntityDeletion({
            versionId,
            providerId,
            regionId: version.region_id,
            entityType: 'categories',
            entityId: categoryId,
            entityData: {
              id: categoryToDelete.id,
              name: categoryToDelete.name,
              parent_id: categoryToDelete.parent_id,
              display_order: categoryToDelete.display_order,
              price: categoryToDelete.price,
              description: categoryToDelete.description,
              is_addon: categoryToDelete.is_addon
            }
          });
        } catch (trackingError) {
          console.error('Failed to track category deletion:', trackingError);
        }
      }
    }
  },

  updateDisplayOrder: async ({ updates }: UpdateCategoryOrderParams) => {
    // Use individual updates instead of RPC
    for (const update of updates) {
      const { error } = await supabase
        .from('categories')
        .update({ display_order: update.display_order })
        .eq('id', update.id);
      
      if (error) throw error;
    }
  }
};
